/**
 * Authentication Hooks for Nour Al-Arabia
 *
 * Convenience hooks for accessing auth state:
 * - User information
 * - Authentication status
 * - Role-based permission checks
 * - Loading and initialization states
 */

import { useCallback } from 'react';
import {
  useAuthStore,
  ROLE_HIERARCHY,
  hasRoleLevel,
  hasAnyRole,
} from '@/stores/authStore';
import type { UserInfo, UserRole } from '@/types/api';

// =============================================================================
// User Hooks
// =============================================================================

/**
 * Get the current authenticated user
 *
 * @returns The current user or null if not authenticated
 *
 * @example
 * const user = useUser();
 * if (user) {
 *   console.log(`Hello, ${user.name}!`);
 * }
 */
export function useUser(): UserInfo | null {
  return useAuthStore((state) => state.user);
}

/**
 * Check if a user is currently authenticated
 *
 * Considers a user authenticated if:
 * - User info exists in state
 * - Access token exists in memory
 *
 * @returns true if user is authenticated
 *
 * @example
 * const isAuthenticated = useIsAuthenticated();
 * if (!isAuthenticated) {
 *   return <Navigate to="/login" />;
 * }
 */
export function useIsAuthenticated(): boolean {
  return useAuthStore((state) => state.user !== null && state.accessToken !== null);
}

// =============================================================================
// Role Hooks
// =============================================================================

/**
 * Check if the current user is an admin
 *
 * @returns true if user has ADMIN role
 *
 * @example
 * const isAdmin = useIsAdmin();
 * if (isAdmin) {
 *   return <AdminDashboard />;
 * }
 */
export function useIsAdmin(): boolean {
  return useAuthStore((state) => state.user?.role === 'ADMIN');
}

/**
 * Check if the current user is a teacher (or admin)
 *
 * Teachers and admins both have teacher-level permissions.
 *
 * @returns true if user is TEACHER or ADMIN
 *
 * @example
 * const isTeacher = useIsTeacher();
 * if (isTeacher) {
 *   return <TeacherGradebook />;
 * }
 */
export function useIsTeacher(): boolean {
  return useAuthStore((state) =>
    state.user?.role === 'TEACHER' || state.user?.role === 'ADMIN'
  );
}

/**
 * Check if the current user is a student
 *
 * @returns true if user has STUDENT role
 *
 * @example
 * const isStudent = useIsStudent();
 * if (isStudent) {
 *   return <StudentDashboard />;
 * }
 */
export function useIsStudent(): boolean {
  return useAuthStore((state) => state.user?.role === 'STUDENT');
}

/**
 * Check if the current user has any of the specified roles
 *
 * @param roles - Array of roles to check against
 * @returns true if user has any of the specified roles
 *
 * @example
 * const canManageUsers = useHasRole(['ADMIN', 'TEACHER']);
 * if (canManageUsers) {
 *   return <UserManagement />;
 * }
 */
export function useHasRole(roles: UserRole[]): boolean {
  return useAuthStore((state) => hasAnyRole(state.user?.role, roles));
}

/**
 * Check if the current user has at least the specified role level
 *
 * Role hierarchy: ADMIN > TEACHER > STUDENT
 *
 * @param minRole - Minimum required role
 * @returns true if user's role meets or exceeds the minimum
 *
 * @example
 * // Check if user can access teacher features
 * const hasTeacherAccess = useHasRoleLevel('TEACHER');
 *
 * // Admin would pass this check
 * // Teacher would pass this check
 * // Student would fail this check
 */
export function useHasRoleLevel(minRole: UserRole): boolean {
  return useAuthStore((state) => hasRoleLevel(state.user?.role, minRole));
}

/**
 * Get the current user's role
 *
 * @returns The user's role or undefined if not authenticated
 *
 * @example
 * const role = useUserRole();
 * console.log(`User role: ${role}`); // 'ADMIN', 'TEACHER', 'STUDENT', or undefined
 */
export function useUserRole(): UserRole | undefined {
  return useAuthStore((state) => state.user?.role);
}

// =============================================================================
// State Hooks
// =============================================================================

/**
 * Get the auth loading state
 *
 * @returns true if a login/logout operation is in progress
 *
 * @example
 * const isLoading = useAuthLoading();
 * if (isLoading) {
 *   return <LoadingSpinner />;
 * }
 */
export function useAuthLoading(): boolean {
  return useAuthStore((state) => state.isLoading);
}

/**
 * Get the auth initialization state
 *
 * @returns true if auth state has been checked on app load
 *
 * @example
 * const isInitialized = useAuthInitialized();
 * if (!isInitialized) {
 *   return <AppLoadingScreen />;
 * }
 */
export function useAuthInitialized(): boolean {
  return useAuthStore((state) => state.isInitialized);
}

/**
 * Get the last authentication error
 *
 * @returns The error object or null
 *
 * @example
 * const error = useAuthError();
 * if (error) {
 *   return <ErrorMessage message={error.message} />;
 * }
 */
export function useAuthError() {
  return useAuthStore((state) => state.error);
}

// =============================================================================
// Action Hooks
// =============================================================================

/**
 * Get auth actions (login, logout, etc.)
 *
 * @returns Object with auth action functions
 *
 * @example
 * const { login, logout, clearError } = useAuthActions();
 *
 * const handleLogin = async (credentials) => {
 *   try {
 *     await login(credentials);
 *     navigate('/dashboard');
 *   } catch (error) {
 *     console.error('Login failed:', error);
 *   }
 * };
 */
export function useAuthActions() {
  const login = useAuthStore((state) => state.login);
  const logout = useAuthStore((state) => state.logout);
  const clearError = useAuthStore((state) => state.clearError);
  const initialize = useAuthStore((state) => state.initialize);

  return {
    login,
    logout,
    clearError,
    initialize,
  };
}

/**
 * Get the initialize function for app startup
 *
 * @returns The initialize function
 *
 * @example
 * const initialize = useAuthInitialize();
 *
 * useEffect(() => {
 *   initialize();
 * }, [initialize]);
 */
export function useAuthInitialize() {
  return useAuthStore((state) => state.initialize);
}

// =============================================================================
// Combined Hook
// =============================================================================

/**
 * Get all auth state and actions in one hook
 *
 * For components that need full auth context.
 *
 * @returns Full auth state and actions
 *
 * @example
 * const {
 *   user,
 *   isAuthenticated,
 *   isLoading,
 *   isInitialized,
 *   error,
 *   login,
 *   logout,
 * } = useAuth();
 */
export function useAuth() {
  const user = useAuthStore((state) => state.user);
  const accessToken = useAuthStore((state) => state.accessToken);
  const isLoading = useAuthStore((state) => state.isLoading);
  const isInitialized = useAuthStore((state) => state.isInitialized);
  const error = useAuthStore((state) => state.error);
  const login = useAuthStore((state) => state.login);
  const logout = useAuthStore((state) => state.logout);
  const clearError = useAuthStore((state) => state.clearError);
  const initialize = useAuthStore((state) => state.initialize);

  const isAuthenticated = user !== null && accessToken !== null;
  const isAdmin = user?.role === 'ADMIN';
  const isTeacher = user?.role === 'TEACHER' || user?.role === 'ADMIN';
  const isStudent = user?.role === 'STUDENT';

  const checkHasRole = useCallback(
    (roles: UserRole[]) => hasAnyRole(user?.role, roles),
    [user?.role]
  );

  const checkHasRoleLevel = useCallback(
    (minRole: UserRole) => hasRoleLevel(user?.role, minRole),
    [user?.role]
  );

  return {
    // State
    user,
    isAuthenticated,
    isLoading,
    isInitialized,
    error,
    // Role checks
    isAdmin,
    isTeacher,
    isStudent,
    role: user?.role,
    hasRole: checkHasRole,
    hasRoleLevel: checkHasRoleLevel,
    // Actions
    login,
    logout,
    clearError,
    initialize,
  };
}

// =============================================================================
// Export role hierarchy for external use
// =============================================================================

export { ROLE_HIERARCHY };
